import { getGetOpportunityByIdQueryOptions } from "@fp/admin-api-client/client";
import type { OpportunityDto } from "@fp/admin-api-client/types/opportunityDto.js";
import { Button } from "@fp/ui/components/base/buttons/button";
import { PageLoading } from "@fp/ui/components/base/page-layout/page-layout";
import { AdminLayout } from "@/components/admin-layout";

import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useCallback } from "react";
import { toast } from "sonner";

interface SuccessPageProps {
	opportunity: OpportunityDto;
}

export const SuccessPageContent = ({ opportunity }: SuccessPageProps) => {
	const router = useRouter();

	const onShare = useCallback(async () => {
		const shareUrl = `${window.location.origin}/opps/${opportunity.id}/invite`;
		if (navigator.share) {
			try {
				await navigator.share({
					title: "Join my FieldPay Opportunity",
					text: `Join my FieldPay Opportunity: ${opportunity.title}`,
					url: shareUrl,
				});
			} catch {
				// User cancelled or share failed, copy to clipboard as fallback
				await navigator.clipboard.writeText(shareUrl);
				toast.success("Link copied to clipboard");
			}
		} else if (navigator.clipboard) {
			// No share API, copy to clipboard
			await navigator.clipboard.writeText(shareUrl);
			toast.success("Link copied to clipboard");
		} else {
			toast.info(
				<div>
					Sharing not supported on this browser, please copy{" "}
					<a
						href={shareUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="underline"
					>
						this link
					</a>{" "}
					and share with your team.
				</div>,
			);
		}
	}, [opportunity.id, opportunity.title]);

	return (
		<div className="flex flex-col  overflow-auto">
			<div className="flex flex-col px-6 bg-fieldpay-blue-500 text-white pb-30">
				<div className="text-center space-y-8 mt-8">
					<h1 className="text-4xl font-bold leading-tight">Opp Shared!</h1>
					<div className="mt-20">
						<div className="flex justify-center items-center mb-16">
							<div className="relative">
								<div className="w-16 h-16 bg-white rounded-full opacity-90 animate-pulse"></div>

								<div className="absolute -top-4 -left-4 w-4 h-4 bg-white rounded-full opacity-70 animate-bounce"></div>
								<div className="absolute -top-6 right-2 w-3 h-3 bg-white rounded-full opacity-60 animate-pulse delay-300"></div>
								<div className="absolute -bottom-4 -right-4 w-5 h-5 bg-white rounded-full opacity-80 animate-bounce delay-150"></div>
								<div className="absolute -bottom-6 left-0 w-2 h-2 bg-white rounded-full opacity-50 animate-pulse delay-500"></div>
								<div className="absolute top-0 -right-8 w-3 h-3 bg-white rounded-full opacity-70 animate-bounce delay-700"></div>
								<div className="absolute bottom-2 -left-8 w-4 h-4 bg-white rounded-full opacity-60 animate-pulse delay-200"></div>

								<div className="absolute -top-8 left-8 text-white text-2xl opacity-80 animate-spin">
									✦
								</div>
								<div className="absolute -bottom-8 right-6 text-white text-lg opacity-70 animate-spin delay-300">
									✦
								</div>
								<div className="absolute top-8 -left-12 text-white text-xl opacity-60 animate-spin delay-600">
									✦
								</div>
							</div>
						</div>
					</div>
					<h2 className="text-2xl font-semibold">Now let's invite your team</h2>
				</div>
			</div>

			<div className="flex-1 flex bg-fieldpay-blue-500">
				<div className="w-full bg-white rounded-t-3xl px-8 pt-20 pb-4 shadow-lg relative">
					<div className="text-center space-y-6 absolute h-30 left-1/2 -translate-x-1/2 top-[-45px]">
						<div className="absolute bottom-0 left-1/2 -translate-x-[100px] shadow-md w-17 h-17 border-2 border-white rounded-full bg-[url('/hvac-blue.png')] bg-position-[-28px_-5px] bg-size-[125px] bg-clip-border bg-image bg-no-repeat"></div>
						<div className="absolute bottom-0 left-1/2 ml-8 shadow-md w-17 h-17 border-2 border-white rounded-full bg-[url('/hvac-orange.png')] bg-position-[-28px_-5px] bg-size-[125px] bg-clip-border bg-image bg-no-repeat"></div>
						<div className="absolute bottom-[20px] left-1/2 -translate-x-1/2 shadow-md w-25 h-25 border-2 border-white rounded-full bg-[url('/pool-pro.png')] bg-position-[-50px_-20px] bg-size-[200px] bg-clip-border bg-image bg-no-repeat"></div>
					</div>
					<div>
						<p className="font-semibold text-center text-lg mb-4">
							Copy the link and send this Opp to your team
						</p>
						<p className="mb-8">
							When they sign up we'll build a Roster for you to use with all
							your other Opps
						</p>
					</div>
					<div className="flex flex-col items-center gap-2 min-h-0">
						<Button
							type="button-primary"
							className="w-1/2 min-w-[200px] m-auto py-4"
							onClick={onShare}
						>
							Share Invite Link
						</Button>
						<Button
							color="secondary"
							className="w-1/2 min-w-[200px] m-auto py-4 mt-4"
							onClick={() =>
								router.navigate({
									to: "/opps/$oppId",
									params: { oppId: opportunity.id },
								})
							}
						>
							I'm done sharing
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export const Route = createFileRoute("/opps/$oppId/edit/success")({
	component: RouteComponent,
	loader: async ({ params, context: { queryClient } }) => {
		const opportunity = await queryClient.fetchQuery(
			getGetOpportunityByIdQueryOptions(params.oppId),
		);
		return opportunity.data;
	},
	pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
	const opportunity = Route.useLoaderData();

	return (
		<AdminLayout className="bg-gradient-to-b from-blue-300 to-blue-400">
			<SuccessPageContent opportunity={opportunity} />
		</AdminLayout>
	);
}
